import React, { useEffect, useState } from "react";
import Link from "next/link";
import Head from "next/head";
import { fetchArticles } from "../../../utils/article";
import { useRouter } from "next/router";
import { Alert, Avatar, Chip, Typography } from "@material-tailwind/react";
import RightSidenav from "../../../sections/right-side-menu";
import { DocsNavbar, Footer } from "@widgets";

const fetchRecentPosts = (articles: any[], numPosts = 5) => {
  const sortedArticles = articles.sort(
    (a, b) =>
      new Date(b.frontMatter.date).getTime() -
      new Date(a.frontMatter.date).getTime(),
  );

  return sortedArticles.slice(0, numPosts);
};

interface TagPageProps {
  articles: Article[];
  tag: string;
  recentPosts: Article[];
}

export interface Article {
  slug: string;
  frontMatter: {
    articleThumbnail: string;
    title: string;
    description: string;
    tag: string[];
  };
}

const getTopTags = (articles) => {
  const tagCounts = {};

  articles.forEach((article) => {
    const tags = article.frontMatter.tag;

    if (tags) {
      tags.forEach((tag) => {
        if (tagCounts[tag]) {
          tagCounts[tag]++;
        } else {
          tagCounts[tag] = 1;
        }
      });
    }
  });

  const topTags = Object.keys(tagCounts)
    .sort((a, b) => tagCounts[b] - tagCounts[a])
    .slice(0, 5);

  return topTags.map((tag) => ({
    tag,
    count: tagCounts[tag],
  }));
};

export async function getStaticProps({ params }) {
  const tag = params.tag as string;
  const allArticles = fetchArticles();
  const recentPosts = fetchRecentPosts(allArticles);
  const topTags = getTopTags(allArticles);

  // Filter articles that contain the specified tag
  const filteredArticles = allArticles.filter((article) =>
    article.frontMatter.tag.map((t) => t.toLowerCase()).includes(tag),
  );

  return {
    props: {
      articles: filteredArticles,
      tag,
      recentPosts,
      topTags,
    },
  };
}

const TagPage: React.FC<TagPageProps> = ({
  articles,
  recentPosts,
  tag,
  topTags,
  slug,
}) => {
  const router = useRouter();
  const [mobileNav, setMobileNav] = React.useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const OFFER_STORAGE_KEY = "hideOfferBar";

  useEffect(() => {
    const shouldHide = localStorage.getItem(OFFER_STORAGE_KEY);
    if (!shouldHide) {
      setIsVisible(true);
    }

    const hideUntil = Number(shouldHide);
    const now = new Date().getTime();
    if (hideUntil && now > hideUntil) {
      setIsVisible(true);
    }
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    // add 2 days
    const hideUntil = new Date().getTime() + 2 * 24 * 60 * 60 * 1000;
    localStorage.setItem(OFFER_STORAGE_KEY, hideUntil.toString());
  };

  function Icon() {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        className="h-6 w-6"
        stroke-width="2"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M6 18L18 6M6 6l12 12"
        ></path>
      </svg>
    );
  }

  if (router.isFallback) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <Head>
        <title>Tailwind CSS News Related to {tag} - Material Tailwind</title>
        <meta
          name="description"
          content={`We cover the latest news in the Tailwind CSS world related to ${tag}. Check out what’s new in the industry!`}
        />
        <link rel="canonical" href="https://www.material-tailwind.com/blog" />
      </Head>
      <Link href="/blocks">
        <Alert
          variant="ghost"
          className="w-full justify-center rounded-none bg-blue-gray-50"
        >
          <div className="flex flex-wrap items-center justify-center !text-blue-gray-900">
            🔥 The Wait Is Over! Pre-Order the Latest <span className="font-bold mx-1">Material Tailwind V3 PRO</span> Now&nbsp;
            <Link href="/v3/preorder" className="font-bold flex items-center ml-1">
              Check out
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" className="w-4 h-4 ml-1.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
              </svg>

            </Link>
            <button
              className="font-bold ml-10 mb-0 !text-grey-900"
              onClick={() => handleClose()}
            >
              <Icon />
            </button>
          </div>
        </Alert>
      </Link>
      <div className="relative mb-8 h-full w-full bg-white">
        <DocsNavbar slug={slug} setMobileNav={setMobileNav} />
        <div className="px-8 lg:px-52">
          <div className="container mx-auto lg:flex">
            <div className="mt-6 w-full lg:w-[65%] lg:px-8">
              <Typography variant="h1" className="mb-2">
                Tailwind CSS News Related to{" "}
                {tag
                  .split("-")
                  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(" ")}
              </Typography>
              <Typography variant="paragraph" className="mb-8">
                We cover the latest news in the Tailwind CSS world related to{" "}
                {tag}. Check out what’s new in the industry!
              </Typography>
              {articles.map((article) => (
                <div key={article.slug.join("/")} className="mb-12">
                  <a href={`/blog/${article.slug.join("/")}`}>
                    {article.frontMatter.articleThumbnail && (
                      <img
                        src={article.frontMatter.articleThumbnail}
                        alt={article.frontMatter.title}
                        className="mb-8 rounded-lg shadow-xl"
                      />
                    )}
                    <Typography variant="h4">
                      {article.frontMatter.title}
                    </Typography>
                  </a>
                  <Typography variant="paragraph" className="mb-4">
                    {article.frontMatter.description}
                    <a
                      className="ml-2 font-normal text-blue-600"
                      href={`/blog/${article.slug}`}
                    >
                      Read More &rarr;
                    </a>
                  </Typography>

                  <div className="flex items-center border-b pb-6">
                    <Avatar
                      size="sm"
                      src={
                        article.frontMatter.authorAvatar ||
                        "https://raw.githubusercontent.com/creativetimofficial/public-assets/master/blog/default-avatar.png"
                      }
                      alt={article.frontMatter.authorName}
                      className="shadow"
                    />
                    <div>
                      <Typography variant="h6" className="ml-2">
                        {article.frontMatter.authorName}
                      </Typography>
                      {article.frontMatter.tags &&
                        Array.isArray(article.frontMatter.tags) && (
                          <Typography
                            variant="small"
                            color="gray"
                            className="font-normal"
                          >
                            {article.frontMatter.tags.join(", ")}
                          </Typography>
                        )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="ml-auto w-full lg:w-80">
              <RightSidenav recentPosts={recentPosts} topTags={topTags} />
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export async function getStaticPaths() {
  const allArticles = fetchArticles();
  const tagsSet = new Set<string>();

  allArticles.forEach((article) => {
    const tags = article.frontMatter.tag;
    if (tags) {
      tags.forEach((tag) => tagsSet.add(tag));
    }
  });

  const paths = Array.from(tagsSet).map((tag) => ({
    params: { tag: tag.toLowerCase().replace(/\s/g, "-") },
  }));

  return { paths, fallback: false };
}

export default TagPage;
